import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Upload, Loader2, CheckCircle2, ImagePlus } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import api, { API, formatApiError } from "@/lib/api";

const CHUNK = 2 * 1024 * 1024;
const MAX = 150 * 1024 * 1024;

export default function TestimonialForm({ item, onSaved, onCancel }) {
  const [form, setForm] = useState({ titulo: item?.titulo || "", nome: item?.nome || "", video_url: item?.video_url || "", ordem: item?.ordem ?? 0, ativo: item?.ativo ?? true });
  const [file, setFile] = useState(null);
  const [capa, setCapa] = useState(null);
  const [capaPreview, setCapaPreview] = useState(item?.capa ? `${API}/files/${item.capa}` : null);
  const [progress, setProgress] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pick = (f) => {
    if (!f) return;
    if (f.size > MAX) return toast.error("Vídeo muito grande (máx. 150 MB).");
    if (!/\.(mp4|m4v|mov|webm)$/i.test(f.name)) return toast.error("Envie um vídeo MP4 ou WEBM.");
    setFile(f);
    setForm((x) => ({ ...x, video_url: "" }));
  };

  const uploadChunks = async () => {
    const uploadId = crypto.randomUUID();
    const total = Math.ceil(file.size / CHUNK);
    setProgress(0);
    for (let i = 0; i < total; i++) {
      const fd = new FormData();
      fd.append("upload_id", uploadId);
      fd.append("index", i);
      fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK));
      await api.post("/admin/video/chunk", fd);
      setProgress(Math.round(((i + 1) / total) * 100));
    }
    return uploadId;
  };

  const pickCapa = (f) => {
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) return toast.error("Imagem muito grande (máx. 5 MB).");
    if (!/\.(jpe?g|png)$/i.test(f.name)) return toast.error("Envie uma imagem JPG ou PNG.");
    setCapa(f);
    setCapaPreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.titulo.trim()) return toast.error("Informe o título.");
    if (!file && !form.video_url.trim() && !item?.tem_video) return toast.error("Envie um vídeo ou informe um link do YouTube.");
    setSaving(true);
    try {
      const payload = { ...form, ordem: parseInt(form.ordem) || 0 };
      if (file) {
        payload.upload_id = await uploadChunks();
        payload.filename = file.name;
      }
      let saved;
      if (item) saved = await api.put(`/admin/testemunhos/${item.id}`, payload);
      else saved = await api.post("/admin/testemunhos", payload);
      if (capa) {
        const fd = new FormData();
        fd.append("file", capa);
        await api.post(`/admin/testemunhos/${saved.data.id}/capa`, fd);
      }
      toast.success(item ? "Testemunho atualizado!" : "Testemunho cadastrado!");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
      setProgress(null);
    }
  };

  return (
    <form onSubmit={submit} data-testid="testimonial-form" className="space-y-4">
      <div>
        <Label className="text-base font-semibold text-slate-700">Título *</Label>
        <Input data-testid="testimonial-titulo" value={form.titulo} onChange={set("titulo")} placeholder="Ex.: Deus restaurou minha família" className="mt-1.5 h-12 text-base rounded-xl" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-base font-semibold text-slate-700">Nome de quem testemunha</Label>
          <Input data-testid="testimonial-nome" value={form.nome} onChange={set("nome")} placeholder="Ex.: Irmã Maria" className="mt-1.5 h-12 text-base rounded-xl" />
        </div>
        <div>
          <Label className="text-base font-semibold text-slate-700">Ordem</Label>
          <Input data-testid="testimonial-ordem" type="number" value={form.ordem} onChange={set("ordem")} className="mt-1.5 h-12 text-base rounded-xl" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-base font-semibold text-slate-700">Vídeo (MP4/WEBM, até 150 MB)</Label>
          <label htmlFor="testimonial-upload" className="mt-1.5 flex items-center justify-center gap-2 h-12 rounded-xl border-2 border-dashed border-slate-300 hover:border-blue-400 text-slate-600 cursor-pointer transition-colors text-sm bg-white">
            {file ? <><CheckCircle2 className="w-4 h-4 text-emerald-600" /> {file.name}</> : item?.tem_video ? <><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Vídeo atual — tocar para trocar</> : <><Upload className="w-4 h-4" /> Escolher vídeo</>}
          </label>
          <input id="testimonial-upload" data-testid="testimonial-upload" type="file" accept="video/mp4,video/webm,.mp4,.mov,.m4v,.webm" className="hidden" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
          {progress !== null && (
            <div className="mt-2 h-2 rounded-full bg-slate-200 overflow-hidden"><div className="h-full bg-blue-700 transition-[width]" style={{ width: `${progress}%` }} /></div>
          )}
        </div>
        <div>
          <Label className="text-base font-semibold text-slate-700">Ou link do YouTube</Label>
          <Input data-testid="testimonial-video_url" value={form.video_url} onChange={(e) => { set("video_url")(e); setFile(null); }} placeholder="https://youtube.com/watch?v=..." className="mt-1.5 h-12 text-base rounded-xl" />
        </div>
      </div>
      <div>
        <Label className="text-base font-semibold text-slate-700">Imagem de capa (JPG/PNG, até 5 MB · opcional)</Label>
        <div className="mt-1.5 flex items-center gap-3">
          <label htmlFor="testimonial-capa" className="flex-1 flex items-center justify-center gap-2 h-12 rounded-xl border-2 border-dashed border-slate-300 hover:border-blue-400 text-slate-600 cursor-pointer transition-colors text-sm bg-white">
            <ImagePlus className="w-4 h-4" /> {capa ? capa.name : capaPreview ? "Capa atual — tocar para trocar" : "Escolher imagem de capa"}
          </label>
          <input id="testimonial-capa" data-testid="testimonial-capa" type="file" accept="image/jpeg,image/png,.jpg,.jpeg,.png" className="hidden" onChange={(e) => { pickCapa(e.target.files?.[0]); e.target.value = ""; }} />
          {capaPreview && <img data-testid="testimonial-capa-preview" src={capaPreview} alt="Capa" className="w-16 h-20 rounded-lg object-cover border border-slate-200 shrink-0" />}
        </div>
        <p className="mt-1 text-xs text-slate-400">Aparece no carrossel antes do play. Recomendado formato vertical (ex.: 1080×1350).</p>
      </div>
      <div className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3 border border-slate-200">
        <Label className="text-base font-semibold text-slate-700">Exibir no carrossel</Label>
        <Switch data-testid="testimonial-ativo" checked={form.ativo} onCheckedChange={(v) => setForm((f) => ({ ...f, ativo: v }))} />
      </div>
      <div className="flex gap-2">
        <button type="submit" data-testid="testimonial-submit" disabled={saving} className="flex-1 py-3.5 rounded-xl bg-blue-800 hover:bg-blue-700 disabled:opacity-60 text-white font-bold text-base shadow-md transition-colors flex items-center justify-center gap-2">
          {saving && <Loader2 className="w-5 h-5 animate-spin" />}
          {progress !== null ? `Enviando ${progress}%` : item ? "SALVAR" : "CADASTRAR"}
        </button>
        {onCancel && (
          <button type="button" data-testid="testimonial-cancel" onClick={onCancel} className="px-5 py-3.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold border border-slate-200">Cancelar</button>
        )}
      </div>
    </form>
  );
}
